import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";
import SectionLayout from "../components/SectionLayout";
import ChartCard from "../components/charts/ChartCard";
import { Grafico, ejes, tooltipStyle, legendProps, barCursor, ChartLoading, ChartError, CHART_SERIES } from "../components/charts/chartTheme";
import { loadCSV, DATA } from "../services/csvLoader";
import { BRAND, SECTION_COLORS } from "../theme";

const INDICADORES = [
  { id: "homicidios",    label: "Víctimas de homicidio (tasa c/100k hab)" },
  { id: "casos_pol",     label: "Casos policiales por familia delictual" },
  { id: "dmcs",          label: "Casos policiales DMCS" },
  { id: "victimizacion", label: "Victimización de hogares (ENUSC)" },
  { id: "percepcion",    label: "Percepción de aumento del delito" },
];

const PALETA = CHART_SERIES;

function useCSV(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    loadCSV(url)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(String(e)); setLoading(false); });
  }, [url]);
  return { data, loading, error };
}

/* Homicidios: víctimas (barras) + tasa (línea, eje secundario) */
function GraficoHomicidios({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.seguridad.homicidiosTasa);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({
    año: r.anio, "Víctimas": r.victimas, "Tasa c/100 mil hab.": r.tasa_100mil,
  }));

  return (
    <Grafico width="100%" height={300}>
      <ComposedChart data={mapped} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis yAxisId="izq" {...ejes.y} />
        <YAxis yAxisId="der" orientation="right" {...ejes.y} domain={[0, "auto"]} />
        <Tooltip {...tooltipStyle} cursor={barCursor} />
        <Legend {...legendProps} />
        <Bar yAxisId="izq" dataKey="Víctimas" fill={BRAND.fesRed} radius={[4, 4, 0, 0]} maxBarSize={40} />
        <Line yAxisId="der" type="monotone" dataKey="Tasa c/100 mil hab." stroke={BRAND.nodoBlue} strokeWidth={2} dot />
      </ComposedChart>
    </Grafico>
  );
}

/* Casos policiales por familia delictual (multilínea) */
function GraficoFamilias({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.seguridad.casosFamilias);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const familias = [...new Set(data.map((r) => r.familia))];
  const años = [...new Set(data.map((r) => r.anio))].sort();
  const mapped = años.map((a) => {
    const fila = { año: a };
    for (const f of familias) {
      const r = data.find((x) => x.anio === a && x.familia === f);
      if (r) fila[f] = r.casos;
    }
    return fila;
  });

  return (
    <Grafico width="100%" height={360}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
        <Tooltip {...tooltipStyle} formatter={(v) => v.toLocaleString("es-CL")} />
        <Legend {...legendProps} wrapperStyle={{ ...legendProps.wrapperStyle, fontSize: 11 }} />
        {familias.map((f, i) => (
          <Line key={f} type="monotone" dataKey={f} stroke={PALETA[i % PALETA.length]} dot={false} strokeWidth={2} />
        ))}
      </LineChart>
    </Grafico>
  );
}

/* DMCS: total anual (suma de grupos históricos) + desglose */
function GraficoDMCS({ setCSV }) {
  const { data, loading, error } = useCSV(DATA.seguridad.dmcsGrupos);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const años = [...new Set(data.map((r) => r.anio))].sort();
  const mapped = años.map((a) => ({
    año: a,
    "Total DMCS": data.filter((r) => r.anio === a).reduce((s, r) => s + (r.casos || 0), 0),
  }));

  return (
    <Grafico width="100%" height={300}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => v.toLocaleString("es-CL")} />
        <Bar dataKey="Total DMCS" fill={BRAND.fesRed} radius={[4, 4, 0, 0]} maxBarSize={48} />
      </BarChart>
    </Grafico>
  );
}

/* ENUSC: barras por año */
function GraficoENUSC({ campos, etiquetas, colores, setCSV }) {
  const { data, loading, error } = useCSV(DATA.seguridad.enusc);
  useEffect(() => { if (data.length) setCSV(data); }, [data, setCSV]);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data
    .map((r) => {
      const fila = { año: r.anio };
      campos.forEach((c, i) => { fila[etiquetas[i]] = r[c] === "" ? undefined : r[c]; });
      return fila;
    })
    // Descarta años sin ninguno de los campos pedidos (p. ej. victimización
    // de hogares/violenta, cuestionario nuevo: solo existe desde 2023).
    .filter((fila) => etiquetas.some((e) => fila[e] !== undefined));

  return (
    <Grafico width="100%" height={280}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => `${v}%`} />
        <Legend {...legendProps} />
        {etiquetas.map((e, i) => (
          <Bar key={e} dataKey={e} fill={colores[i]} radius={[4, 4, 0, 0]} barSize={48} />
        ))}
      </BarChart>
    </Grafico>
  );
}

function ConCSV({ render, ...cardProps }) {
  const [csv, setCSV] = useState([]);
  return (
    <ChartCard {...cardProps} csvData={csv.length ? csv : undefined}>
      {render(setCSV)}
    </ChartCard>
  );
}

export default function Seguridad() {
  return (
    <SectionLayout
      title="Seguridad"
      description="Indicadores de violencia, delincuencia y percepción ciudadana. Fuentes: Centro para la Prevención de Homicidios, CEAD (Ministerio de Seguridad Pública), ENUSC y Fiscalía de Chile."
      tag="Seguridad Ciudadana"
      palette={SECTION_COLORS.seguridad}
      indicadores={INDICADORES}
    >
      <Box id="homicidios">
        <ConCSV
          title="Víctimas de homicidio consumado (tasa c/100.000 hab)"
          subtitle="Cifra oficial validada interinstitucionalmente por el Observatorio de Homicidios. Tasa calculada con proyecciones de población INE."
          fuente="Observatorio de Homicidios — Ministerio de Seguridad Pública"
          tag="Anual"
          csvFilename="homicidios_tasa"
          pngFilename="homicidios_tasa"
          render={(setCSV) => (
            <>
              <GraficoHomicidios setCSV={setCSV} />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                Distinta de las causas del Ministerio Público y de los casos policiales: solo víctimas
                con muerte confirmada por medios oficiales.
              </Typography>
            </>
          )}
        />
      </Box>

      <Box id="casos_pol">
        <ConCSV
          title="Casos policiales por familias delictuales"
          subtitle="Denuncias y detenciones conocidas por Carabineros y PDI, total país, por familia delictual."
          fuente="CEAD — Ministerio de Seguridad Pública"
          tag="Anual (desfase ~3 meses)"
          csvFilename="casos_policiales_familias"
          pngFilename="casos_policiales_familias"
          render={(setCSV) => <GraficoFamilias setCSV={setCSV} />}
        />
      </Box>

      <Box id="dmcs">
        <ConCSV
          title="Casos policiales — Delitos de Mayor Connotación Social (DMCS)"
          subtitle="Suma de los grupos delictuales de la categoría histórica DMCS (homicidios, delitos sexuales, lesiones, robos y hurtos). Total país."
          fuente="CEAD — Ministerio de Seguridad Pública"
          tag="Anual"
          csvFilename="dmcs"
          pngFilename="dmcs"
          render={(setCSV) => <GraficoDMCS setCSV={setCSV} />}
        />
      </Box>

      <Box id="victimizacion">
        <ConCSV
          title="Victimización de hogares (ENUSC)"
          subtitle="% de hogares urbanos víctimas de algún delito y de delitos violentos en los últimos 12 meses."
          fuente="ENUSC — INE y Ministerio de Seguridad Pública"
          tag="Anual"
          csvFilename="victimizacion_hogares"
          pngFilename="victimizacion_hogares"
          render={(setCSV) => (
            <>
              <GraficoENUSC
                campos={["victimizacion_hogares", "victimizacion_violenta"]}
                etiquetas={["Victimización general", "Victimización violenta"]}
                colores={[BRAND.nodoBlue, BRAND.fesRed]}
                setCSV={setCSV}
              />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                El cuestionario fue rediseñado en 2023: la serie no es comparable con ediciones anteriores.
              </Typography>
            </>
          )}
        />
      </Box>

      <Box id="percepcion">
        <ConCSV
          title="Percepción de aumento del delito en el país"
          subtitle="% de personas que considera que la delincuencia aumentó en el país durante los últimos 12 meses."
          fuente="ENUSC — INE y Ministerio de Seguridad Pública"
          tag="Anual"
          csvFilename="percepcion_delito"
          pngFilename="percepcion_delito"
          render={(setCSV) => (
            <GraficoENUSC
              campos={["percepcion_aumento_pais"]}
              etiquetas={["Percibe aumento de la delincuencia"]}
              colores={[BRAND.fesRed]}
              setCSV={setCSV}
            />
          )}
        />
      </Box>
    </SectionLayout>
  );
}
